import { supabase } from '@/lib/supabase'
import type { Recipe, RecipeFormData } from '@/types/recipe'
import { mapRecipeInput, requireSessionUser, sortNewestFirst } from './api-utils'

const getRecipes = async (): Promise<Recipe[]> => {
  const { data, error } = await supabase
    .from('recipes')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) {
    throw error
  }

  return data ?? []
}

const getRecipe = async (id: string): Promise<Recipe> => {
  const { data, error } = await supabase
    .from('recipes')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    throw error
  }

  return data
}

const createRecipe = async (recipe: RecipeFormData): Promise<Recipe> => {
  const user = await requireSessionUser()
  const payload = mapRecipeInput(recipe, user.id)

  const { data, error } = await supabase
    .from('recipes')
    .insert(payload)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data
}

const updateRecipe = async (id: string, updates: Partial<RecipeFormData>): Promise<Recipe> => {
  const { data, error } = await supabase
    .from('recipes')
    .update(mapRecipeInput(updates))
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data
}

const deleteRecipe = async (id: string): Promise<void> => {
  const { error } = await supabase.from('recipes').delete().eq('id', id)
  if (error) {
    throw error
  }
}

const getMealPlanSuggestions = async (_householdId: string, mealType?: string): Promise<Recipe[]> => {
  const recipes = await getRecipes()
  return sortNewestFirst(
    recipes.filter((recipe) => !mealType || recipe.meal_type === mealType)
  ).slice(0, 10)
}

export const recipeApi = {
  getRecipes,
  getRecipe,
  createRecipe,
  updateRecipe,
  deleteRecipe,
  getMealPlanSuggestions,
}
